// FLAG:useDocuAi — the „Art" was changed to something nobody reviews.
//
// DocuAI analyses every document and opens a review for an invoice
// (documentInvoiceDetails.reviewStatus = 'review'). If the user then reclassifies it —
// Rechnungseingang → Rechnungsausgang, Sonstiges, Vertrag … — there is nothing left to review: the
// review entry point disappears with the new „Art" (isReviewableInvoiceArt, `hostsInvoiceDetails`
// in document-drawer.tsx), yet the row stayed on „Zu prüfen" forever — a task nobody CAN finish,
// because the mask that would finish it is no longer reachable.
//
// The reclassification therefore IS the review: it closes as a document-only review
// (`reviewProvenance: 'userVerified'`, exactly like the review mask's own save in
// receipt-edit.tsx), and `bookingTarget: 'document'` tells the server that neither an expense nor a
// revenue comes out of it (resolveReviewedDocumentTarget → 'document' → skipped: 'documentOnly').
import { isPendingInvoiceReview } from '@/modules/document/utils/invoice-analysis-state'
import { isReviewableInvoiceArt } from '@/modules/document/utils/document-invoice-kind'

export interface ArtChangeReviewClosure {
  reviewStatus: string,
  reviewScope: string,
  reviewProvenance: string,
  bookingTarget: string,
  requiresAccountingReview: boolean,
  positionsReviewed: boolean,
}

// The server derives `reviewScope` / `requiresAccountingReview` / `positionsReviewed` from
// `bookingTarget: 'document'` itself (fileService/update.js) and forces 'complete' through
// `pendingReview` anyway. They are stated here regardless: the intent belongs in the request, not
// in a silent server default that may change.
export const DOCUMENT_ONLY_REVIEW_CLOSURE: ArtChangeReviewClosure = {
  reviewStatus: 'complete',
  reviewScope: 'document',
  reviewProvenance: 'userVerified',
  bookingTarget: 'document',
  requiresAccountingReview: false,
  positionsReviewed: false,
}

// The „Art" is the document's system tag — the one the select on the „Allgemein" tab replaces
// (`updateDocumentKind`, which compares by label too).
const artTagLabel = file => (file?.tags || []).find(({ isSystem }) => !!isSystem)?.label ?? null

export const hasArtChanged = (document, initialDocument) =>
  artTagLabel(document) !== artTagLabel(initialDocument)

/**
 * Does THIS save close an open review because the „Art" was reclassified?
 *
 * Deliberately tied to an actual CHANGE, not to "the current Art is not reviewable": otherwise any
 * save (rename, tag) would tick off an open review on the side, and a document whose Art never
 * fitted in the first place would lose its review on the next click on anything.
 */
export const closesReviewByArtChange = (document, initialDocument) =>
  hasArtChanged(document, initialDocument)
  && isReviewableInvoiceArt(initialDocument)
  && !isReviewableInvoiceArt(document)
  && isPendingInvoiceReview(document)

/**
 * The documentInvoiceDetails part the drawer save has to carry — or `null` when this
 * reclassification closes no review. Deliberately only the closing fields: an upsert writes exactly
 * the columns it is given, so the analysed values stay as they are (nobody ever confirmed them, and
 * a reclassification must not confirm them either).
 */
export const buildArtChangeReviewClosure = (document, initialDocument): ArtChangeReviewClosure | null =>
  closesReviewByArtChange(document, initialDocument) ? { ...DOCUMENT_ONLY_REVIEW_CLOSURE } : null
